import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader, Edit2, X, Plus } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import GatewayCredentialFields, { SECTION, FIELD, blankGatewayProfile } from './GatewayFields';

// Settings → Payments → Entity Gateways. One payment gateway account per GTPL entity: a
// customer whose CATV entity has an enabled account here pays through it, ahead of the
// Online PG tabs and Gateway Routing (backend services/gatewayRouter.js). Entities with no
// account here keep paying the way they do today.
// Stored as an ordinary gateway profile with `entity` set, so the credential form, masking
// and API are the same ones the Gateway Routing tab uses.

const ENTITIES = ['SPBP0042', 'SPBP0368', 'SPBP0369', 'SPBP0370'];
const emptyUi = { showSecret: false, showWebhookSecret: false, showAblepaySalt: false };

export default function EntityGatewaysTab() {
  const [profiles, setProfiles] = useState([]);
  const [fallbackId, setFallbackId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editUi, setEditUi] = useState(emptyUi);
  const [saving, setSaving] = useState(false);
  const [extra, setExtra] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/payment-gateway-profiles');
      setProfiles(data.data.profiles || []);
      setFallbackId(data.data.serviceRouting?.catv?.profile || null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load gateways');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setEditing(p => ({ ...p, [k]: v }));
  const setFee = (method, field, val) => setEditing(p => ({
    ...p, feesByMethod: { ...p.feesByMethod, [method]: { ...p.feesByMethod[method], [field]: val } },
  }));

  const startEdit = (entity) => {
    const existing = profiles.find(p => p.entity === entity);
    setEditing(existing
      ? { ...blankGatewayProfile(), ...existing }
      : { ...blankGatewayProfile(), name: entity, entity });
    setEditUi(emptyUi);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing._id) await api.put(`/settings/payment-gateway-profiles/${editing._id}`, editing);
      else await api.post('/settings/payment-gateway-profiles', editing);
      toast.success(`Gateway saved for ${editing.entity || 'all others'}`);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save gateway');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="card"><div className="card-body"><Loader size={16} className="spin" /> Loading…</div></div>;

  // The CableTV account pinned on the (hidden) Gateway Routing page: every customer whose
  // entity has no enabled gateway above pays through it (gatewayRouter.resolveForNewOrder).
  const fallback = profiles.find(p => String(p._id) === String(fallbackId));

  const entities = [...ENTITIES, ...profiles.map(p => p.entity).filter(e => e && !ENTITIES.includes(e))];

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Entity Payment Gateways</h3>
          <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', margin: '4px 0 12px', maxWidth: 640 }}>
            Give each entity its own payment gateway account. Customers of that entity pay through it, for every
            service and amount. An entity with no gateway here uses the "All others" gateway.
          </p>

          {entities.map(entity => {
            const p = profiles.find(x => x.entity === entity);
            return (
              <div key={entity} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '.92rem' }}>{entity}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                    {p ? `${p.provider}${p.testMode ? ' · test mode' : ''}${p.ablepayPgApiUrl ? ` · ${p.ablepayPgApiUrl}` : ''}` : 'Not set — uses the normal routing'}
                  </div>
                </div>
                {p && <span className={`badge ${p.isEnabled ? 'badge-success' : 'badge-secondary'}`}>{p.isEnabled ? 'Enabled' : 'Disabled'}</span>}
                <button className="btn btn-secondary btn-sm" onClick={() => startEdit(entity)}>
                  {p ? <Edit2 size={12} /> : <><Plus size={12} style={{ marginRight: 4 }} />Set up</>}
                </button>
              </div>
            );
          })}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '.92rem' }}>All others</div>
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                {fallback ? `Entities without their own gateway, and customers with no entity: ${fallback.name} · ${fallback.provider}` : 'No fallback gateway set'}
              </div>
            </div>
            {fallback && <span className={`badge ${fallback.isEnabled ? 'badge-success' : 'badge-secondary'}`}>{fallback.isEnabled ? 'Enabled' : 'Disabled'}</span>}
            {fallback && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing({ ...blankGatewayProfile(), ...fallback }); setEditUi(emptyUi); }}>
                <Edit2 size={12} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <input className="form-control" style={{ width: 160 }} placeholder="Other entity code" value={extra}
              onChange={e => setExtra(e.target.value.toUpperCase().trim())} />
            <button className="btn btn-secondary btn-sm" disabled={!extra} onClick={() => { startEdit(extra); setExtra(''); }}>Add entity</button>
          </div>
        </div>
      </div>

      {editing && (
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--primary)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Gateway for {editing.entity || 'all others'}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}><X size={13} /></button>
            </div>
            <SECTION title="Account">
              <FIELD label="Name" required>
                <input className="form-control" style={{ maxWidth: 400 }} value={editing.name}
                  onChange={e => setField('name', e.target.value)} />
              </FIELD>
            </SECTION>
            <GatewayCredentialFields pg={editing} ui={editUi} setField={setField}
              setUiField={(k, v) => setEditUi(u => ({ ...u, [k]: v }))} setFeeMethod={setFee} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !editing.name?.trim()}>
                <Save size={14} style={{ marginRight: 6 }} />{saving ? 'Saving…' : 'Save Gateway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
